import { type HTMLAttributes, type KeyboardEvent, useRef } from 'react'

import { cn } from '../cn'

// Tabs — horizontal sub-view switcher, URL-synced (design/components/tabs.html).
// The active tab carries the brand blue in text AND a 2px underline (never
// colour alone). Full ARIA tabs pattern with roving tabindex + automatic
// activation (←→ move + select, Home/End to ends). Panels are rendered by the
// caller; wire them with <TabPanel idBase tabId> using the same idBase.

export interface TabItem {
  id: string
  label: string
  count?: number
  futureTag?: '1.5' | '2'
  disabled?: boolean
}

export interface TabsProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange'> {
  tabs: TabItem[]
  activeId: string
  onChange: (id: string) => void
  /** Shared id prefix that ties tabs to their <TabPanel idBase>. */
  idBase: string
  'aria-label': string
}

/** In-page sub-view switcher on the full ARIA tabs pattern: roving tabindex,
 *  automatic activation (←/→ select, Home/End, disabled tabs skipped). The
 *  caller renders panels via <TabPanel> sharing the same `idBase`; for
 *  cross-route nav use links, not tabs. */
export function Tabs({ tabs, activeId, onChange, idBase, className, ...rest }: TabsProps) {
  const refs = useRef<(HTMLButtonElement | null)[]>([])

  // Walk from `index` in the given direction, skipping disabled tabs, and
  // select the first enabled one. The direction follows the pressed key, not
  // the sign of the index, so wrapping past either end keeps going the right way.
  function moveTo(index: number, dir: 1 | -1) {
    const n = tabs.length
    for (let step = 0; step < n; step++) {
      const i = ((index % n) + n) % n
      const t = tabs[i]
      if (t && !t.disabled) {
        onChange(t.id)
        refs.current[i]?.focus()
        return
      }
      index += dir
    }
  }

  function onKeyDown(e: KeyboardEvent<HTMLButtonElement>, current: number) {
    switch (e.key) {
      case 'ArrowRight':
        e.preventDefault()
        moveTo(current + 1, 1)
        break
      case 'ArrowLeft':
        e.preventDefault()
        moveTo(current - 1, -1)
        break
      case 'Home':
        e.preventDefault()
        moveTo(0, 1)
        break
      case 'End':
        e.preventDefault()
        moveTo(tabs.length - 1, -1)
        break
    }
  }

  return (
    <div
      role="tablist"
      className={cn(
        'flex gap-0.5 overflow-x-auto border-b border-line [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
        className,
      )}
      {...rest}
    >
      {tabs.map((t, i) => {
        const active = t.id === activeId
        const ariaLabel = [
          t.label,
          t.count != null ? `${t.count}` : null,
          t.futureTag ? `Fase ${t.futureTag}` : null,
        ]
          .filter(Boolean)
          .join(', ')
        return (
          <button
            key={t.id}
            ref={(el) => {
              refs.current[i] = el
            }}
            type="button"
            role="tab"
            id={`${idBase}-tab-${t.id}`}
            aria-controls={`${idBase}-panel-${t.id}`}
            aria-selected={active}
            aria-label={t.count != null || t.futureTag ? ariaLabel : undefined}
            tabIndex={active ? 0 : -1}
            disabled={t.disabled}
            onClick={() => !t.disabled && onChange(t.id)}
            onKeyDown={(e) => onKeyDown(e, i)}
            className={cn(
              '-mb-px inline-flex items-center gap-[7px] whitespace-nowrap border-b-2 border-transparent px-[13px] py-[11px] text-[12.5px] font-semibold transition-colors',
              'focus-visible:rounded-[4px] focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand-blue',
              active ? 'border-brand-blue text-link' : 'text-text-2 hover:text-text',
              t.disabled && 'cursor-not-allowed text-text-3 hover:text-text-3',
            )}
          >
            {t.label}
            {t.count != null && (
              <span
                aria-hidden
                className={cn(
                  'rounded-pill px-[7px] py-px text-[10px] font-bold tabular-nums',
                  active
                    ? 'bg-[color-mix(in_srgb,var(--brand-blue)_12%,transparent)] text-link'
                    : 'bg-field text-text-2',
                )}
              >
                {t.count}
              </span>
            )}
            {t.futureTag && (
              <span
                aria-hidden
                className="rounded-pill bg-warn-bg px-1.5 py-px text-[9px] font-bold tracking-[0.03em] text-warn"
              >
                Fase {t.futureTag}
              </span>
            )}
          </button>
        )
      })}
    </div>
  )
}

export interface TabPanelProps extends HTMLAttributes<HTMLDivElement> {
  idBase: string
  tabId: string
}

export function TabPanel({ idBase, tabId, className, children, ...rest }: TabPanelProps) {
  return (
    <div
      role="tabpanel"
      id={`${idBase}-panel-${tabId}`}
      aria-labelledby={`${idBase}-tab-${tabId}`}
      tabIndex={0}
      className={cn('focus-visible:outline-none', className)}
      {...rest}
    >
      {children}
    </div>
  )
}
